
import React from 'react';
import { Link } from 'react-router-dom';

const PatientPharmacyVisionPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/beast-blog" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-green-600 rounded-full flex items-center justify-center">
                <i className="ri-capsule-fill text-white text-xl"></i>
              </div>
              <span className="text-xl font-bold text-gray-900">ビースト君ブログ</span>
            </Link>
            <nav className="flex space-x-6">
              <Link to="/home" className="text-gray-600 hover:text-blue-600 transition-colors">ホーム</Link>
              <Link to="/services" className="text-gray-600 hover:text-blue-600 transition-colors">サービス</Link>
              <Link to="/beast-blog" className="text-gray-600 hover:text-blue-600 transition-colors">ブログ</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Article Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-4">
              <i className="ri-calendar-line mr-2"></i>
              2025年11月8日
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">患者のための薬局ビジョン</h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              患者中心の薬局サービスが目指す未来像と、薬剤師の新たな役割について
            </p>
          </div>

          {/* Keywords Section */}
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <i className="ri-key-2-line text-blue-600 mr-2"></i>
              キーワード
            </h2>
            <div className="flex flex-wrap gap-2">
              {[
                '患者中心医療', '薬局ビジョン', 'かかりつけ薬剤師', '服薬指導',
                'セルフメディケーション', '在宅医療', '地域包括ケア', 'QOL向上',
                '薬物療法管理', 'ヘルスケア', '予防医学', '多職種連携'
              ].map((keyword, index) => (
                <span key={index} className="px-3 py-1 bg-white text-blue-700 rounded-full text-sm font-medium border border-blue-200">
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Article Content */}
        <article className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          {/* Introduction */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-heart-pulse-line text-red-500 mr-3"></i>
              患者中心の薬局サービスとは
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                現代の薬局は、単に薬を調剤・販売する場所から、患者さん一人ひとりの健康を総合的にサポートする「ヘルスケアステーション」へと進化しています。患者のための薬局ビジョンは、<strong>「患者さんの生活の質（QOL）向上」</strong>を最優先に考え、個々のニーズに応じたきめ細やかなサービスを提供することを目指しています。
              </p>
              <p className="text-gray-700 leading-relaxed">
                このビジョンの実現により、患者さんは安心して治療に専念でき、より良い健康状態を維持できるようになります。
              </p>
            </div>
          </section>

          {/* Core Vision */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-eye-line text-blue-500 mr-3"></i>
              薬局ビジョンの3つの柱
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-user-heart-line text-white text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-blue-900 mb-3">個別化医療の推進</h3>
                <p className="text-blue-800 text-sm leading-relaxed">
                  患者さん一人ひとりの体質、生活習慣、価値観に合わせた最適な薬物療法を提案し、個別化された医療サービスを提供します。
                </p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-team-line text-white text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-green-900 mb-3">多職種連携の強化</h3>
                <p className="text-green-800 text-sm leading-relaxed">
                  医師、看護師、ケアマネジャーなど他の医療従事者と密接に連携し、チーム医療の一員として患者さんをサポートします。
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-white text-xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-purple-900 mb-3">安全性の確保</h3>
                <p className="text-purple-800 text-sm leading-relaxed">
                  薬物相互作用のチェック、副作用モニタリング、適切な服薬指導により、安全で効果的な薬物療法を実現します。
                </p>
              </div>
            </div>
          </section>

          {/* Patient-Centered Services */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-service-line text-orange-500 mr-3"></i>
              患者中心のサービス内容
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6 border-l-4 border-orange-400">
                <h3 className="text-xl font-semibold text-orange-900 mb-3 flex items-center">
                  <i className="ri-user-settings-line text-orange-600 mr-2"></i>
                  かかりつけ薬剤師制度
                </h3>
                <p className="text-orange-800 leading-relaxed mb-4">
                  専任の薬剤師が患者さんの服薬歴を一元管理し、継続的な薬学的ケアを提供します。24時間相談対応により、いつでも安心して相談できる体制を整えています。
                </p>
                <ul className="text-orange-800 text-sm space-y-2">
                  <li className="flex items-center"><i className="ri-check-line text-orange-600 mr-2"></i>服薬歴の一元管理</li>
                  <li className="flex items-center"><i className="ri-check-line text-orange-600 mr-2"></i>24時間電話相談対応</li>
                  <li className="flex items-center"><i className="ri-check-line text-orange-600 mr-2"></i>定期的な服薬状況確認</li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6 border-l-4 border-teal-400">
                <h3 className="text-xl font-semibold text-teal-900 mb-3 flex items-center">
                  <i className="ri-home-heart-line text-teal-600 mr-2"></i>
                  在宅医療サポート
                </h3>
                <p className="text-teal-800 leading-relaxed mb-4">
                  通院が困難な患者さんのご自宅を訪問し、服薬指導や薬剤管理を行います。患者さんの生活環境に合わせた最適な服薬方法を提案します。
                </p>
                <ul className="text-teal-800 text-sm space-y-2">
                  <li className="flex items-center"><i className="ri-check-line text-teal-600 mr-2"></i>訪問服薬指導</li>
                  <li className="flex items-center"><i className="ri-check-line text-teal-600 mr-2"></i>薬剤管理指導</li>
                  <li className="flex items-center"><i className="ri-check-line text-teal-600 mr-2"></i>介護者への指導</li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border-l-4 border-indigo-400">
                <h3 className="text-xl font-semibold text-indigo-900 mb-3 flex items-center">
                  <i className="ri-medicine-bottle-line text-indigo-600 mr-2"></i>
                  セルフメディケーション支援
                </h3>
                <p className="text-indigo-800 leading-relaxed mb-4">
                  OTC医薬品の適切な選択から健康食品の相談まで、患者さんの自己健康管理をトータルサポートします。予防医学の観点から健康維持をお手伝いします。
                </p>
                <ul className="text-indigo-800 text-sm space-y-2">
                  <li className="flex items-center"><i className="ri-check-line text-indigo-600 mr-2"></i>OTC医薬品の選択支援</li>
                  <li className="flex items-center"><i className="ri-check-line text-indigo-600 mr-2"></i>健康食品の相談</li>
                  <li className="flex items-center"><i className="ri-check-line text-indigo-600 mr-2"></i>生活習慣改善指導</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Technology Integration */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-smartphone-line text-green-500 mr-3"></i>
              デジタル技術の活用
            </h2>
            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6">
              <p className="text-gray-700 leading-relaxed mb-6">
                最新のデジタル技術を活用し、より便利で効率的な薬局サービスを提供します。患者さんの利便性向上と薬剤師の業務効率化を同時に実現します。
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <i className="ri-smartphone-line text-blue-500 mr-2"></i>
                    オンライン服薬指導
                  </h4>
                  <p className="text-gray-600 text-sm">
                    ビデオ通話による遠隔服薬指導で、時間や場所の制約を受けずに専門的な指導を受けられます。
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <i className="ri-qr-code-line text-green-500 mr-2"></i>
                    電子お薬手帳
                  </h4>
                  <p className="text-gray-600 text-sm">
                    スマートフォンアプリで服薬記録を管理し、複数の医療機関での情報共有を円滑に行います。
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <i className="ri-notification-line text-orange-500 mr-2"></i>
                    服薬リマインダー
                  </h4>
                  <p className="text-gray-600 text-sm">
                    AIを活用した服薬タイミングの通知により、飲み忘れを防止し、治療効果を最大化します。
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <i className="ri-database-line text-purple-500 mr-2"></i>
                    データ分析
                  </h4>
                  <p className="text-gray-600 text-sm">
                    服薬データの分析により、個々の患者さんに最適化された薬物療法を提案します。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Future Vision */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-rocket-line text-purple-500 mr-3"></i>
              未来の薬局像
            </h2>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                患者のための薬局ビジョンが実現された未来では、薬局は地域の健康拠点として、以下のような役割を果たします：
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-heart-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-purple-900 mb-1">健康増進センター</h4>
                      <p className="text-purple-800 text-sm">予防医学を重視し、病気になる前の健康維持をサポート</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-community-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-purple-900 mb-1">地域連携ハブ</h4>
                      <p className="text-purple-800 text-sm">医療機関、介護施設、行政との連携を強化</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-brain-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-purple-900 mb-1">AI活用拠点</h4>
                      <p className="text-purple-800 text-sm">人工知能を活用した高度な薬物療法管理</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-global-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-purple-900 mb-1">グローバル対応</h4>
                      <p className="text-purple-800 text-sm">多言語対応と国際的な医療情報の共有</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Conclusion */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-lightbulb-line text-yellow-500 mr-3"></i>
              まとめ
            </h2>
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200">
              <p className="text-gray-700 leading-relaxed mb-4">
                患者のための薬局ビジョンは、単なる理想論ではなく、現在進行形で実現されつつある現実です。薬剤師一人ひとりが患者さんの立場に立ち、真摯に向き合うことで、このビジョンは必ず実現できます。
              </p>
              <p className="text-gray-700 leading-relaxed">
                私たち薬剤師は、患者さんの健康と幸福を第一に考え、常に学び続け、進化し続けることで、真に患者さんのための薬局を作り上げていきましょう。
              </p>
            </div>
          </section>
        </article>

        {/* Related Articles */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <i className="ri-links-line text-blue-500 mr-2"></i>
            関連記事
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/beast-blog/medical-plan-5diseases-6projects" className="block p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
              <h4 className="font-semibold text-blue-900 mb-2">第８次医療計画の５疾病６事業について</h4>
              <p className="text-blue-700 text-sm">地域完結型医療における薬局薬剤師の役割</p>
            </Link>
            <Link to="/beast-blog/family-pharmacist-guide" className="block p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
              <h4 className="font-semibold text-green-900 mb-2">かかりつけ薬剤師になるためのガイド</h4>
              <p className="text-green-700 text-sm">患者さんに信頼される薬剤師になるために</p>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-green-600 rounded-full flex items-center justify-center">
                  <i className="ri-capsule-fill text-white"></i>
                </div>
                <span className="text-lg font-bold">ビースト君ブログ</span>
              </div>
              <p className="text-gray-400 text-sm">
                薬剤師のための情報発信プラットフォーム
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">カテゴリー</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/beast-blog" className="hover:text-white transition-colors">最新記事</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">サービス</Link></li>
                <li><Link to="/health-consultation" className="hover:text-white transition-colors">健康相談</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">サポート</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/information" className="hover:text-white transition-colors">お知らせ</Link></li>
                <li><Link to="/career" className="hover:text-white transition-colors">キャリア</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">フォローする</h4>
              <div className="flex space-x-4">
                <a href="#" className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-600 transition-colors">
                  <i className="ri-twitter-line text-sm"></i>
                </a>
                <a href="#" className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-600 transition-colors">
                  <i className="ri-facebook-line text-sm"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-gray-400 text-sm">
              © 2025 ビースト君ブログ. All rights reserved. | 
              <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors ml-1">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PatientPharmacyVisionPage;
